import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Shield, Mail, Globe, Lock, Database, UserCheck } from 'lucide-react';

const PrivacyPage = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-black/95 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white hover:text-primary transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span>Volver</span>
          </Link>
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
              <span className="text-black font-bold text-sm">P</span>
            </div>
            <span className="text-white font-bold">PLEYADES</span>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-primary/10 rounded-xl">
              <Shield className="w-8 h-8 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">Política de Privacidad</h1>
              <p className="text-zinc-400">Última actualización: Marzo 2026</p>
            </div>
          </div>
        </div>

        <div className="prose prose-invert prose-zinc max-w-none">
          {/* Section 1 */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-primary mb-4 flex items-center gap-2">
              <UserCheck className="w-5 h-5" />
              1. Responsable del tratamiento
            </h2>
            <div className="bg-zinc-900/50 rounded-xl p-6 border border-white/5">
              <ul className="space-y-2 text-zinc-300">
                <li className="flex items-center gap-2">
                  <span className="text-zinc-500">Responsable:</span>
                  <span className="text-white font-medium">Pleyades Parking</span>
                </li>
                <li className="flex items-center gap-2">
                  <Globe className="w-4 h-4 text-zinc-500" />
                  <span className="text-zinc-500">Web:</span>
                  <a href="https://pleyadesparking.es" className="text-primary hover:underline">pleyadesparking.es</a>
                </li>
                <li className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-zinc-500" />
                  <span className="text-zinc-500">Contacto:</span>
                  <a href="mailto:julioviv@pleyadesparking.es" className="text-primary hover:underline">julioviv@pleyadesparking.es</a>
                </li>
              </ul>
            </div>
          </section>

          {/* Section 2 */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-primary mb-4 flex items-center gap-2">
              <Database className="w-5 h-5" />
              2. Datos que recopilamos
            </h2>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="bg-zinc-900/50 rounded-xl p-5 border border-white/5">
                <h3 className="text-white font-medium mb-3">Identificación y contacto</h3>
                <ul className="text-zinc-400 text-sm space-y-1">
                  <li>• Nombre</li>
                  <li>• Email</li>
                  <li>• Teléfono</li>
                </ul>
              </div>
              <div className="bg-zinc-900/50 rounded-xl p-5 border border-white/5">
                <h3 className="text-white font-medium mb-3">Datos de reserva</h3>
                <ul className="text-zinc-400 text-sm space-y-1">
                  <li>• Fechas/horas</li>
                  <li>• Matrícula</li>
                  <li>• Canal de origen (si aplica)</li>
                  <li>• Estado de pago</li>
                </ul>
              </div>
              <div className="bg-zinc-900/50 rounded-xl p-5 border border-white/5">
                <h3 className="text-white font-medium mb-3">Datos operativos</h3>
                <ul className="text-zinc-400 text-sm space-y-1">
                  <li>• Check-in/check-out</li>
                  <li>• QR/tokens</li>
                  <li>• Notas internas del servicio</li>
                </ul>
              </div>
              <div className="bg-zinc-900/50 rounded-xl p-5 border border-white/5">
                <h3 className="text-white font-medium mb-3">Datos técnicos</h3>
                <ul className="text-zinc-400 text-sm space-y-1">
                  <li>• IP, user agent</li>
                  <li>• Registros de seguridad</li>
                  <li>• Logs de auditoría</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 3 */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-primary mb-4">3. Finalidades</h2>
            <p className="text-zinc-300 mb-4">Tratamos tus datos para:</p>
            <ol className="space-y-3 text-zinc-300">
              <li className="flex gap-4">
                <span className="flex-shrink-0 w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center text-primary font-bold text-sm">1</span>
                <span>Gestionar reservas y prestar el servicio de valet parking.</span>
              </li>
              <li className="flex gap-4">
                <span className="flex-shrink-0 w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center text-primary font-bold text-sm">2</span>
                <span>Enviar comunicaciones operativas (confirmación, cambios, recordatorios).</span>
              </li>
              <li className="flex gap-4">
                <span className="flex-shrink-0 w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center text-primary font-bold text-sm">3</span>
                <span>Gestión de pagos y facturación (si aplica).</span>
              </li>
              <li className="flex gap-4">
                <span className="flex-shrink-0 w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center text-primary font-bold text-sm">4</span>
                <span>Seguridad, prevención de fraude y auditoría (incluyendo integraciones de reservas por correo).</span>
              </li>
              <li className="flex gap-4">
                <span className="flex-shrink-0 w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center text-primary font-bold text-sm">5</span>
                <span>Enviar promociones y descuentos <strong className="text-white">solo si has dado tu consentimiento</strong>.</span>
              </li>
            </ol>
          </section>

          {/* Section 4 */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-primary mb-4">4. Base legal</h2>
            <ul className="space-y-3 text-zinc-300">
              <li className="flex items-start gap-3">
                <span className="text-primary font-bold">•</span>
                <div>
                  <strong className="text-white">Ejecución de un contrato / medidas precontractuales:</strong>
                  <span className="text-zinc-400"> gestión de la reserva y prestación del servicio.</span>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary font-bold">•</span>
                <div>
                  <strong className="text-white">Cumplimiento de obligaciones legales</strong>
                  <span className="text-zinc-400"> (cuando aplique).</span>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary font-bold">•</span>
                <div>
                  <strong className="text-white">Interés legítimo:</strong>
                  <span className="text-zinc-400"> seguridad de la plataforma y prevención de fraude.</span>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary font-bold">•</span>
                <div>
                  <strong className="text-white">Consentimiento:</strong>
                  <span className="text-zinc-400"> envío de comunicaciones comerciales/promocionales.</span>
                </div>
              </li>
            </ul>
          </section>

          {/* Section 5 */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-primary mb-4">5. Destinatarios y encargados</h2>
            <p className="text-zinc-300 mb-4">Podemos compartir datos con proveedores necesarios para operar:</p>
            <ul className="space-y-2 text-zinc-300 ml-4 mb-4">
              <li className="flex items-start gap-2">
                <span className="text-primary">•</span>
                <span><strong className="text-white">Pasarela de pago</strong> (por ejemplo, Stripe) cuando pagas online.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary">•</span>
                <span><strong className="text-white">Proveedor de email</strong> (por ejemplo, Resend para envíos; Mailgun para recepción/automatización de reservas por correo).</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary">•</span>
                <span><strong className="text-white">Canales/partners</strong> (Parkos/Onepark/etc.) cuando la reserva proviene de ellos o requiere conciliación.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary">•</span>
                <span><strong className="text-white">Proveedores de hosting</strong> e infraestructura.</span>
              </li>
            </ul>
            <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4">
              <p className="text-green-400 font-medium">No vendemos tus datos a terceros.</p>
            </div>
          </section>

          {/* Section 6 */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-primary mb-4">6. Conservación</h2>
            <ul className="space-y-3 text-zinc-300">
              <li className="flex items-start gap-3">
                <span className="text-primary">•</span>
                <span><strong className="text-white">Datos de reserva:</strong> el tiempo necesario para prestar el servicio y cumplir obligaciones legales.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary">•</span>
                <span><strong className="text-white">Logs y auditorías:</strong> el tiempo razonable para seguridad y trazabilidad.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary">•</span>
                <span><strong className="text-white">Marketing:</strong> mientras mantengas tu consentimiento y no solicites la baja.</span>
              </li>
            </ul>
          </section>

          {/* Section 7 */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-primary mb-4 flex items-center gap-2">
              <Lock className="w-5 h-5" />
              7. Derechos del usuario
            </h2>
            <p className="text-zinc-300 mb-4">Puedes solicitar:</p>
            <div className="grid gap-3 md:grid-cols-3">
              {['Acceso', 'Rectificación', 'Supresión', 'Oposición', 'Limitación', 'Portabilidad'].map((right) => (
                <div key={right} className="bg-zinc-900/50 rounded-lg p-3 border border-white/5 text-center">
                  <span className="text-white font-medium">{right}</span>
                </div>
              ))}
            </div>
            <p className="text-zinc-300 mt-4">
              Retirar tu consentimiento de marketing en cualquier momento (enlace de baja en emails o por contacto).
            </p>
            <div className="mt-4 bg-zinc-900/50 rounded-xl p-4 border border-white/5">
              <p className="text-zinc-400">
                <strong className="text-white">Contacto:</strong>{' '}
                <a href="mailto:julioviv@pleyadesparking.es" className="text-primary hover:underline">julioviv@pleyadesparking.es</a>
              </p>
            </div>
          </section>

          {/* Section 8 */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-primary mb-4">8. Baja de comunicaciones comerciales</h2>
            <p className="text-zinc-300 mb-4">
              En cada email promocional incluimos un enlace para darte de baja. Al darte de baja:
            </p>
            <ul className="space-y-2 text-zinc-300 ml-4">
              <li className="flex items-start gap-2">
                <span className="text-primary">•</span>
                <span>dejaremos de enviarte promociones,</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary">•</span>
                <span>podremos conservar un registro mínimo para evitar futuros envíos no deseados.</span>
              </li>
            </ul>
          </section>

          {/* Section 9 */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-primary mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5" />
              9. Seguridad
            </h2>
            <p className="text-zinc-300">
              Aplicamos medidas técnicas y organizativas razonables para proteger la información (control de accesos por roles, validación de webhooks, registros de auditoría, etc.).
            </p>
          </section>

          {/* Section 10 */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-primary mb-4">10. Cookies</h2>
            <p className="text-zinc-300">
              El uso de cookies se detalla en nuestro banner de cookies al visitar el sitio. Utilizamos:
            </p>
            <ul className="space-y-2 text-zinc-300 mt-4 ml-4">
              <li className="flex items-start gap-2">
                <span className="text-primary">•</span>
                <span><strong className="text-white">Cookies necesarias:</strong> para el funcionamiento básico del sitio.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary">•</span>
                <span><strong className="text-white">Cookies analíticas:</strong> para entender cómo se usa el sitio (solo con consentimiento).</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary">•</span>
                <span><strong className="text-white">Cookies de marketing:</strong> para personalizar anuncios (solo con consentimiento).</span>
              </li>
            </ul>
            <p className="text-zinc-400 mt-4 text-sm">
              Puedes configurar tu consentimiento en cualquier momento a través del banner de cookies.
            </p>
          </section>

          {/* Section 11 */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-primary mb-4">11. Cambios</h2>
            <p className="text-zinc-300">
              Podemos actualizar esta Política para reflejar cambios operativos o legales. La versión vigente estará publicada en la web.
            </p>
          </section>
        </div>

        {/* Footer links */}
        <div className="mt-12 pt-8 border-t border-white/10 flex flex-wrap gap-4 justify-center">
          <Link to="/terminos" className="text-primary hover:underline">Términos y Condiciones</Link>
          <span className="text-zinc-600">|</span>
          <Link to="/" className="text-primary hover:underline">Volver al inicio</Link>
          <span className="text-zinc-600">|</span>
          <a href="mailto:julioviv@pleyadesparking.es" className="text-primary hover:underline">Contacto</a>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPage;
